import React, { useEffect, useState } from "react";
import { ym } from "../lib/dates";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Printer, ChartLine, CheckCircle, Clock, Warning } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import api, { formatIDR, formatApiError, formatMonth } from "../lib/api";
import { generatePDF } from "../lib/pdf";

const STATUS_META = {
  pending: { label: "Belum Ditunaikan", cls: "bg-rose-100 text-rose-700" },
  partial: { label: "Sebagian", cls: "bg-amber-100 text-amber-700" },
  done: { label: "Tuntas", cls: "bg-emerald-100 text-emerald-700" },
};

export default function PosKeuangan() {
  const currentMonth = ym();
  const [rekap, setRekap] = useState(null);
  const [lembagas, setLembagas] = useState([]);
  const [nomens, setNomens] = useState([]);
  const [period, setPeriod] = useState(currentMonth);
  const [filterLembaga, setFilterLembaga] = useState("all");
  const [filterNomen, setFilterNomen] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ lembaga_id: "", nomenklatur_code: "", period: currentMonth, amount_kebutuhan: 0, notes: "" });

  const load = async () => {
    const q = new URLSearchParams();
    if (period) q.set("period", period);
    if (filterLembaga !== "all") q.set("lembaga_id", filterLembaga);
    const [r, l, n] = await Promise.all([
      api.get(`/pos-keuangan/rekap?${q}`),
      api.get("/lembaga").catch(() => ({ data: [] })),
      api.get("/nomenklatur").catch(() => ({ data: [] })),
    ]);
    setRekap(r.data); setLembagas(l.data); setNomens(n.data);
  };
  useEffect(() => { load(); }, [period, filterLembaga]); // eslint-disable-line

  const filtered = (rekap?.rows || []).filter((r) =>
    (filterNomen === "all" || r.nomenklatur_code === filterNomen) &&
    (filterStatus === "all" || r.status === filterStatus)
  );

  const openNew = () => {
    setEditing(null);
    setForm({ lembaga_id: lembagas[0]?.id || "", nomenklatur_code: nomens[0]?.code || "", period, amount_kebutuhan: 0, notes: "" });
    setOpen(true);
  };
  const openEdit = (p) => { setEditing(p); setForm({ lembaga_id: p.lembaga_id, nomenklatur_code: p.nomenklatur_code, period: p.period, amount_kebutuhan: p.amount_kebutuhan, notes: p.notes || "" }); setOpen(true); };

  const save = async () => {
    if (!form.lembaga_id || !form.nomenklatur_code || !form.period) return toast.error("Lembaga, nomenklatur, dan periode wajib");
    try {
      const payload = { ...form, amount_kebutuhan: Number(form.amount_kebutuhan || 0) };
      if (editing) await api.put(`/pos-keuangan/${editing.id}`, payload);
      else await api.post("/pos-keuangan", payload);
      toast.success("Pos tersimpan"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Hapus pos ini?")) return; try { await api.delete(`/pos-keuangan/${id}`); load(); toast.success("Terhapus"); } catch { toast.error("Gagal"); } };

  const lembagaLabel = filterLembaga === "all" ? "Seluruh Lembaga" : (lembagas.find((l) => l.id === filterLembaga)?.name || "-");

  const printPDF = () => {
    if (!rekap) return;
    generatePDF({
      title: "Rekap Pos Keuangan (Kebutuhan Dana Bulanan)",
      subtitle: `${lembagaLabel} — ${formatMonth(period)}`,
      headers: ["Lembaga", "Nomenklatur", "Kebutuhan", "Realisasi", "Sisa", "Status"],
      rows: filtered.map((r) => [r.lembaga_name, `${r.nomenklatur_code} – ${r.nomenklatur_name || ""}`, formatIDR(r.amount_kebutuhan), formatIDR(r.amount_realisasi), formatIDR(r.sisa), STATUS_META[r.status]?.label]),
      meta: {
        "Periode": formatMonth(period), "Lembaga": lembagaLabel, "Jumlah Pos": filtered.length,
        "Total Kebutuhan": formatIDR(filtered.reduce((a, r) => a + r.amount_kebutuhan, 0)),
        "Total Realisasi": formatIDR(filtered.reduce((a, r) => a + r.amount_realisasi, 0)),
        "Sisa": formatIDR(filtered.reduce((a, r) => a + r.sisa, 0)),
      },
      filename: `rekap-pos-keuangan-${period}.pdf`,
    });
  };

  return (
    <div data-testid="pos-keuangan-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1">Keuangan Internal</div>
          <h1 className="font-display text-3xl font-black text-foreground tracking-tight">Pos Keuangan</h1>
          <p className="text-muted-foreground text-sm mt-1">Rekap kebutuhan dana per lembaga per pos (nomenklatur) tiap bulan — realisasi otomatis dari Pengeluaran.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={printPDF} data-testid="pos-print-btn"><Printer size={14} className="mr-1.5" />Cetak PDF</Button>
          <Button size="sm" onClick={openNew} data-testid="pos-add-btn"><Plus size={14} className="mr-1.5" />Tambah Pos</Button>
        </div>
      </div>

      {rekap && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <Stat label="Total Pos" value={rekap.count} icon={ChartLine} />
          <Stat label="Kebutuhan" value={formatIDR(rekap.total_kebutuhan)} tone="info" />
          <Stat label="Realisasi" value={formatIDR(rekap.total_realisasi)} tone="ok" />
          <Stat label="Sisa Kebutuhan" value={formatIDR(rekap.sisa_total)} tone={rekap.sisa_total > 0 ? "danger" : "default"} />
        </div>
      )}

      {rekap && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <MiniStat label="Belum Ditunaikan" value={rekap.by_status?.pending || 0} icon={Warning} tone="danger" />
          <MiniStat label="Sebagian" value={rekap.by_status?.partial || 0} icon={Clock} tone="warn" />
          <MiniStat label="Tuntas" value={rekap.by_status?.done || 0} icon={CheckCircle} tone="ok" />
        </div>
      )}

      <div className="bg-card border border-border rounded-xl p-3 mb-4 flex flex-wrap gap-3 items-end">
        <div><Label className="text-xs">Periode</Label><Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="w-40" data-testid="pos-period" /></div>
        <div><Label className="text-xs">Lembaga</Label>
          <Select value={filterLembaga} onValueChange={setFilterLembaga}>
            <SelectTrigger className="w-52" data-testid="pos-filter-lembaga"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua Lembaga</SelectItem>{lembagas.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Nomenklatur</Label>
          <Select value={filterNomen} onValueChange={setFilterNomen}>
            <SelectTrigger className="w-44" data-testid="pos-filter-nomen"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua</SelectItem>{nomens.map((n) => <SelectItem key={n.code} value={n.code}>{n.code} – {n.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Status</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40" data-testid="pos-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua</SelectItem>{Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-3 font-bold">Lembaga</th>
                <th className="px-3 py-3 font-bold">Nomenklatur</th>
                <th className="px-3 py-3 font-bold text-right">Kebutuhan</th>
                <th className="px-3 py-3 font-bold text-right">Realisasi</th>
                <th className="px-3 py-3 font-bold text-right">Sisa</th>
                <th className="px-3 py-3 font-bold text-center">Progress</th>
                <th className="px-3 py-3 font-bold text-center">Status</th>
                <th className="px-3 py-3 font-bold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const pct = r.amount_kebutuhan > 0 ? Math.min(100, Math.round((r.amount_realisasi / r.amount_kebutuhan) * 100)) : 0;
                return (
                  <tr key={r.id} className="border-t border-border hover:bg-muted/30">
                    <td className="px-3 py-2.5 font-semibold">{r.lembaga_name}</td>
                    <td className="px-3 py-2.5"><div className="font-bold">{r.nomenklatur_code}</div><div className="text-xs text-muted-foreground">{r.nomenklatur_name}</div></td>
                    <td className="px-3 py-2.5 text-right font-mono font-bold tabular-nums">{formatIDR(r.amount_kebutuhan)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-emerald-600 tabular-nums">{formatIDR(r.amount_realisasi)}</td>
                    <td className="px-3 py-2.5 text-right font-mono font-black tabular-nums text-rose-600">{formatIDR(r.sisa)}</td>
                    <td className="px-3 py-2.5"><div className="flex items-center gap-2"><div className="flex-1 h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} /></div><span className="text-xs font-bold tabular-nums w-10 text-right">{pct}%</span></div></td>
                    <td className="px-3 py-2.5 text-center"><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-black ${STATUS_META[r.status]?.cls}`}>{STATUS_META[r.status]?.label}</span></td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(r)} className="p-1.5 hover:bg-muted rounded" data-testid={`pos-edit-${r.id}`}><PencilSimple size={14} /></button>
                      <button onClick={() => remove(r.id)} className="p-1.5 hover:bg-rose-50 rounded text-rose-600" data-testid={`pos-delete-${r.id}`}><Trash size={14} /></button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={8} className="px-5 py-16 text-center text-muted-foreground">Belum ada pos keuangan untuk periode ini</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="pos-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit" : "Tambah"} Pos Keuangan</DialogTitle>
            <DialogDescription>Kebutuhan dana per lembaga per pos untuk periode bulanan. Realisasi otomatis dihitung dari Pengeluaran.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Lembaga *</Label>
              <Select value={form.lembaga_id} onValueChange={(v) => setForm({ ...form, lembaga_id: v })}>
                <SelectTrigger data-testid="pos-lembaga-select"><SelectValue placeholder="Pilih lembaga" /></SelectTrigger>
                <SelectContent>{lembagas.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Nomenklatur *</Label>
              <Select value={form.nomenklatur_code} onValueChange={(v) => setForm({ ...form, nomenklatur_code: v })}>
                <SelectTrigger data-testid="pos-nomen-select"><SelectValue placeholder="Pilih pos" /></SelectTrigger>
                <SelectContent>{nomens.map((n) => <SelectItem key={n.code} value={n.code}>{n.code} – {n.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Periode *</Label><Input type="month" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} data-testid="pos-period-input" /></div>
            <div className="col-span-2"><Label>Kebutuhan (Rp)</Label><Input type="number" value={form.amount_kebutuhan} onChange={(e) => setForm({ ...form, amount_kebutuhan: e.target.value })} data-testid="pos-amount-input" /></div>
            <div className="col-span-2"><Label>Catatan</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} data-testid="pos-save-btn">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, tone = "default", icon: Icon }) {
  const t = { default: "bg-card border-border", info: "bg-blue-50 border-blue-200", ok: "bg-emerald-50 border-emerald-200", danger: "bg-rose-50 border-rose-200" };
  return <div className={`rounded-xl border p-4 ${t[tone]}`}><div className="flex items-center justify-between mb-2"><div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{label}</div>{Icon && <Icon size={16} weight="duotone" className="text-foreground/60" />}</div><div className="font-display text-xl md:text-2xl font-black tracking-tight tabular-nums">{value}</div></div>;
}
function MiniStat({ label, value, icon: Icon, tone }) {
  const t = { ok: "text-emerald-600 bg-emerald-50 border-emerald-200", warn: "text-amber-600 bg-amber-50 border-amber-200", danger: "text-rose-600 bg-rose-50 border-rose-200" };
  return <div className={`rounded-lg border p-3 flex items-center gap-3 ${t[tone]}`}><Icon size={22} weight="duotone" /><div><div className="text-[10px] uppercase font-bold tracking-widest opacity-70">{label}</div><div className="font-display text-xl font-black tabular-nums">{value}</div></div></div>;
}
